import React, { useEffect, useRef, useState } from 'react';
import { postAPI } from '../../../utils/utils';
import FormBottomBar from './FormBottomBar';

// The card payment of an answer sent from the web: the server works out what is
// left to pay from the saved answer (backend/form-card-checkout.js) and the person
// pays on Stripe's page, which brings them back here with ?pagament=<session>.
// `autoStart`: just sent with «Targeta» chosen, so it goes straight to Stripe.
// `lines`: what the answer's tickets cost, for the bar's summary. `renderPage`
// draws the page above it from where the payment stands ({ phase, message, paid }).

// Stripe can take a moment to confirm a payment the person has just made
const CONFIRM_RETRIES = 5;
const CONFIRM_RETRY_MS = 3000;

const post = (endpoint, body) => new Promise((resolve, reject) => {
  postAPI(endpoint, body, resolve, reject);
});

const CardPayment = ({ evId, user, paymentSession, autoStart, lines = [], renderPage }) => {
  const [quote, setQuote] = useState(null);
  const [state, setState] = useState(paymentSession ? 'confirming' : 'loading');
  const [error, setError] = useState(null);
  const started = useRef(false);

  const goToStripe = async () => {
    setState('redirecting');
    setError(null);
    try {
      const response = await post('/stripe/form-checkout/start', {
        formId: evId,
        userId: user,
        returnUrl: `${window.location.origin}${window.location.pathname}`,
      });
      if (response?.url) {
        window.location.assign(response.url);
        return;
      }
      setQuote(response);
      setState('ready');
    } catch (startError) {
      setError(startError?.code === 'CARD_UNAVAILABLE'
        ? "Ara no es pot pagar amb targeta. Modifica la resposta i tria una altra manera de pagar."
        : "No s'ha pogut obrir el pagament. Torna-ho a provar.");
      setState('ready');
    }
  };

  useEffect(() => {
    let cancelled = false;
    let retryId;
    const load = async (attempt = 0) => {
      try {
        const response = paymentSession
          ? await post('/stripe/form-checkout/confirm', { formId: evId, userId: user, sessionId: paymentSession })
          : await post('/stripe/form-checkout/status', { formId: evId, userId: user });
        if (cancelled) return;
        if (paymentSession && response?.state === 'open' && attempt < CONFIRM_RETRIES) {
          retryId = setTimeout(() => load(attempt + 1), CONFIRM_RETRY_MS);
          return;
        }
        setQuote(response);
        setState('ready');
        const owes = response?.available && response?.baseToPay > 0.005;
        if (autoStart && owes && !started.current) {
          started.current = true;
          goToStripe();
        }
      } catch (loadError) {
        if (cancelled) return;
        setError(paymentSession
          ? "No hem pogut confirmar el pagament. Si s'ha cobrat, sortirà a la llista de la colla d'aquí a uns minuts."
          : "No s'ha pogut consultar el pagament amb targeta.");
        setState('ready');
      }
    };
    load();
    return () => {
      cancelled = true;
      clearTimeout(retryId);
    };
    // Asked once per answer shown; goToStripe only reads props
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [evId, user, paymentSession]);

  // Where the answer stands: being sent to Stripe, back from it, still to pay, paid
  const owes = quote?.baseToPay > 0.005;
  const shown = state !== 'loading' && state !== 'confirming' && quote?.card;
  const unavailable = shown && owes && !quote.available;
  const phase = state === 'confirming' ? 'confirming'
    : state === 'redirecting' || (state === 'loading' && autoStart) ? 'opening'
    : shown && quote.fullyPaid ? 'paid'
    : shown && owes ? 'owes'
    : 'sent';
  const message = error || (unavailable ? "Ara no es pot pagar amb targeta. Modifica la resposta i tria una altra manera de pagar." : null);
  const paysHere = shown && owes && quote.available;
  return (
    <>
      {renderPage({ phase, message, paid: quote?.paid || 0 })}
      {/* The bar only while there is a card payment to make: its summary and the button */}
      {paysHere || state === 'redirecting' ? (
        <FormBottomBar
          lines={owes ? [
            ...lines,
            // What an earlier card payment already covered, when the answer grew after paying
            ...(quote.paidBase > 0 ? [{ key: 'already-paid', label: 'Ja pagat', amount: -quote.paidBase }] : []),
            ...(quote.commissionMode === 'user_pays' && quote.commission > 0
              ? [{ key: 'card-commission', label: 'Comissió de la targeta', amount: quote.commission }] : []),
          ] : []}
          total={owes ? quote.finalAmount : null}
          totalLabel={quote?.paidBase > 0 ? 'Falta pagar la diferència' : 'Falta pagar'}
        >
          <button
            type="button"
            className="editable-form-button"
            onClick={goToStripe}
            disabled={state === 'redirecting'}
          >
            {state === 'redirecting' ? 'Obrint el pagament...' : 'Paga amb targeta'}
          </button>
        </FormBottomBar>
      ) : null}
    </>
  );
};

export default CardPayment;
